import styled from '@emotion/styled';
import { Dropdown, Image, Space } from 'antd';
import { ReactNode, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { FaOutdent } from 'react-icons/fa';
import { FiLogOut } from 'react-icons/fi';
import { MdAccountCircle } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  ConnectedUserEntity,
  ProfilType,
} from '../../../entities/ConnectedUserEntity';
import { createForums, createUser } from '../../../redux/userStore/actions';
import { ROUTES } from '../../../routes';
import { ORANGE, SECONDARY, YELLOW } from '../../../shared/colors';
import { defaultImage } from '../../../shared/defaultImage';
import { checkProfile, ProtectedView } from '../ProtectedRoutes';
import { SUB_MENUS } from './Menus';

const NavLink = styled.p`
  font-size: 10px;
  cursor: pointer;
  color: white;
  font-family: Poppins-R;
  margin: 0px;
  position: relative;

  &:hover {
    text-decoration: underline;
    color: ${YELLOW};
  }

  &.active {
    color: ${YELLOW};
    font-weight: bold;
    border-bottom: 2px solid ${YELLOW};
  }

  @media (min-width: 1200px) {
    font-size: 14px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: ${SECONDARY};
  color: white !important;
  padding: 5px 15px 0px 15px;
  height: 50px;

  .connected {
    display: flex;
    align-items: center;
    margin: 0px;
    padding: 2px;
    border: 1px solid white;
    font-family: Poppins-R;
    font-size: 10px;
    border-radius: 20px;
    min-width: 140px;
  }

  > .logo {
    flex: 2;
  }

  > .linkContainer {
    display: none;
  }

  > .menu-button {
    display: block;
    cursor: pointer;
    margin-right: 10px;
  }

  @media (min-width: 768px) {
    > .linkContainer {
      display: flex;
      flex: 8;
      justify-content: flex-end;
      align-items: center;
      padding-left: 0px;

      > * {
        margin-right: 20px;
      }
    }

    > .menu-button {
      display: none;
    }
  }

  @media (min-width: 1200px) {
    > .linkContainer {
      flex: 8;
      // padding-left: 20%;
    }
  }

  .menu {
    height: 300px;
    width: 100%;
    position: fixed;
    z-index: 1;
    top: 0;
    left: 0;
    background-color: ${SECONDARY};
    overflow-x: hidden;
    transition: 0.5s;
    padding: 30px;
  }

  .menu-close {
    height: 0px;
    width: 100%;
    position: fixed;
    z-index: 1;
    top: 0;
    left: 0;
    background-color: ${SECONDARY};
    overflow-x: hidden;
    transition: 0.5s;
  }

  .menu-linkContainer {
    display: flex;
    flex-direction: column;
  }

  .logout {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    font-size: 12px;
    padding: 5px;
    border-radius: 50%;

    &:hover {
      box-shadow: 0 4px 8px 0 rgba(255, 255, 255, 0.3),
        0 6px 20px 0 rgba(255, 255, 255, 0.3);
    }
  }
`;

const SubHeaderContainer = styled.div`
  display: flex;
  justify-content: flex-end; /* ✅ aligne les éléments enfants à droite */
  align-items: center;
  background-color: ${SECONDARY};
  padding: 5px 50px 0px 0px;
  min-height: 40px;
  border-top: 1px solid white;

  .sub-link {
    color: white;
    margin-left: 20px; /* ✅ plutôt que margin-right pour pousser depuis la gauche */
    cursor: pointer;

    &:hover {
      color: ${YELLOW};
    }

    &.active {
      font-weight: bold;
      color: ${YELLOW};
      border-bottom: 2px solid ${YELLOW};
    }
  }
`;

// Ajout pour rendre le menu principal actif quand un sous-menu est actif
const isMainMenuActive = (route: string): boolean => {
  // eslint-disable-next-line no-restricted-globals
  if (location.pathname === route) return true;
  const subMenus = SUB_MENUS[route];
  if (!subMenus) return false;
  return subMenus.some((item) => {
    // eslint-disable-next-line no-restricted-globals
    if (item.path && location.pathname.startsWith(item.path)) return true;
    if (item.dropdown) {
      return item.children.some((child) =>
        // eslint-disable-next-line no-restricted-globals
        location.pathname.startsWith(child.path),
      );
    }
    return false;
  });
};
const MenuContent = ({
  closeMenu,
  other,
}: {
  closeMenu?: () => void;
  other?: ReactNode;
}) => {
  const history = useHistory();
  const location = useLocation();

  const connectedUser: ConnectedUserEntity | null =
    useSelector((state: any) => state?.userReducer?.user) ?? null;

  const dispatch = useDispatch();

  const isActive = (path: string) => location.pathname === path;

  // Helper (peut être dans un utils/acl.ts)
  const hasAnyRole = (profils: any, roles: ProfilType[]) =>
    roles.some((r) => checkProfile(profils, r));

  // Dans ton composant :
  const canSeeMenus =
    !!connectedUser?.authentifie &&
    hasAnyRole(connectedUser?.profils, [
      'resp_commercial',
      'commercial',
      'chef_agence',
      'responsable',
      'gestionnaire',
    ]);

  return (
    <div className={closeMenu ? 'menu-linkContainer' : 'linkContainer'}>
      <NavLink
        className={isActive(ROUTES.HOME_PAGE) ? 'active' : ''}
        onClick={() => {
          closeMenu?.();
          history.push(ROUTES.HOME_PAGE);
        }}
      >
        Accueil
      </NavLink>

      <NavLink
        className={isMainMenuActive(ROUTES.FORMATION.PAGE) ? 'active' : ''}
        onClick={() => {
          closeMenu?.();
          history.push(ROUTES.FORMATION.PAGE);
        }}
      >
        Fiches et Infos
      </NavLink>

      <ProtectedView
        authorization={[
          'resp_commercial',
          'commercial',
          'chef_agence',
          'gestionnaire',
          'admin',
          'sadmin',
        ]}
      >
        <NavLink
          className={isActive(ROUTES.TCHAT.PAGE) ? 'active' : ''}
          onClick={() => {
            closeMenu?.();
            history.push(ROUTES.TCHAT.PAGE);
          }}
        >
          Tchat
        </NavLink>
      </ProtectedView>
      {/* ajoute le menu admin */}
      {connectedUser?.authentifie &&
      checkProfile(connectedUser.profils, 'admin') ? (
        <NavLink
          className={
            isMainMenuActive(ROUTES.PANEL.ADMIN.PARTENAIRE) ? 'active' : ''
          }
          onClick={() => {
            closeMenu?.();
            history.push(ROUTES.PANEL.ADMIN.PARTENAIRE);
          }}
        >
          Administration
        </NavLink>
      ) : (
        <></>
      )}
      {connectedUser?.authentifie &&
        checkProfile(connectedUser?.profils, 'sadmin') && (
          <NavLink
            className={isMainMenuActive(ROUTES.ADMIN.CONSOLE) ? 'active' : ''}
            onClick={() => {
              closeMenu?.();
              history.push(ROUTES.ADMIN.CONSOLE);
            }}
          >
            Administration
          </NavLink>
        )}

      {/* ajoute le menu sadmin */}
      {canSeeMenus && (
        <>
          <NavLink
            className={
              isMainMenuActive(ROUTES.PANEL.CLIENTELE.CLIENTS) ? 'active' : ''
            }
            onClick={() => {
              closeMenu?.();
              history.push(ROUTES.PANEL.CLIENTELE.CLIENTS);
            }}
          >
            Portefeuille client
          </NavLink>

          <NavLink
            className={
              isMainMenuActive(ROUTES.PANEL.EMISSION.BOND) ? 'active' : ''
            }
            onClick={() => {
              closeMenu?.();
              history.push(ROUTES.PANEL.EMISSION.BOND);
            }}
          >
            Portefeuille des titres
          </NavLink>

          <NavLink
            className={isMainMenuActive(ROUTES.DASHBOARD.PAGE) ? 'active' : ''}
            onClick={() => {
              closeMenu?.();
              history.push(ROUTES.DASHBOARD.PAGE);
            }}
          >
            Gestion des Transactions
          </NavLink>
        </>
      )}

      {connectedUser?.authentifie &&
      new Date().getTime() < connectedUser.nextAuthDate ? (
        <>
          <NavLink
            className={isActive(ROUTES.DASHBOARD.PROFIL_KYC) ? 'active' : ''}
            onClick={() => {
              closeMenu?.();
              history.push(ROUTES.DASHBOARD.PROFIL_KYC);
            }}
          >
            <div className='connected'>
              <MdAccountCircle size={30} />
              <span>
                Bienvenue <br />{' '}
                <span style={{ fontSize: 12 }}>
                  {connectedUser.nom} {connectedUser.prenom}
                </span>
              </span>
            </div>
          </NavLink>

          <div
            className='logout'
            onClick={() => {
              dispatch(
                createUser({
                  authentifie: false,
                  id: '',
                  url: '',
                  nom: '',
                  prenom: '',
                  profils: [],
                  manager: { nom: '', code: '', url: '', code_referent: '' },
                  partenaire: { nom: '', url: '', code_referent: '' },
                  email: '',
                  token: '',
                  nextAuthDate: 0,
                  achat: 0,
                  vente: 0,
                }),
              );
              dispatch(createForums([]));
              history.push(ROUTES.HOME_PAGE);
            }}
          >
            <FiLogOut />
            Logout
          </div>
        </>
      ) : (
        <>
          <NavLink onClick={() => {}}>
            {/* <Button
              className='butt'
              style={{
                height: 35,
                width: 100,
                borderRadius: 20,
              }}
              onClick={() => history.push(ROUTES.SIGNUP)}
            >
              <span style={{ fontSize: 14 }}>S'inscrire</span>
            </Button> */}
          </NavLink>
        </>
      )}
    </div>
  );
};

export const SubHeader = () => {
  const location = useLocation();
  const history = useHistory();

  const activeParent = Object.keys(SUB_MENUS).find((parentRoute) => {
    const subMenus = SUB_MENUS[parentRoute];
    return subMenus.some((item) => {
      if (item.path && location.pathname.startsWith(item.path)) return true;
      if (item.dropdown) {
        return item.children.some((child) =>
          location.pathname.startsWith(child.path),
        );
      }
      return false;
    });
  });

  if (!activeParent) return null;

  const currentSub = SUB_MENUS[activeParent];

  return (
    <SubHeaderContainer>
      {currentSub.map((item) =>
        item.dropdown ? (
          <Dropdown
            key={item.key}
            menu={{
              items: item.children.map((child) => ({
                key: child.key,
                label: child.label,
                onClick: () => history.push(child.path),
              })),
            }}
          >
            <div className='sub-link'>{item.label}</div>
          </Dropdown>
        ) : (
          <div
            key={item.key}
            className={`sub-link ${
              location.pathname === item.path ? 'active' : ''
            }`}
            onClick={() => {
              if (item.path) {
                history.push(item.path);
              }
            }}
          >
            {item.label}
          </div>
        ),
      )}
    </SubHeaderContainer>
  );
};

export const Header = ({ other }: { other?: ReactNode }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <>
      <HeaderContainer>
        <Space className='logo'>
          <Image
            alt='moranje-logo'
            src='/assets/images/moranje-logo.png'
            height={45}
            width={60}
            preview={false}
            style={{ objectFit: 'contain', cursor: 'pointer' }}
            fallback={defaultImage}
            // onClick={() => history.push(ROUTES.HOME_PAGE)}
          />
          <h3
            style={{
              color: 'white',
              fontFamily: 'Poppins-B',
              fontSize: 25,
              // marginLeft: 25,
              marginBottom: 0,
            }}
          >
            <span style={{ color: ORANGE }}>Moranj</span>
            <span style={{ color: '#6d4c41' }}>E</span>
            <span style={{ color: YELLOW }}>trade</span>
          </h3>
        </Space>
        <MenuContent other={other} />

        <div className={showMenu ? 'menu' : 'menu-close'}>
          <AiOutlineClose
            size={30}
            color='white'
            style={{ cursor: 'pointer' }}
            onClick={() => setShowMenu(false)}
          />
          <MenuContent closeMenu={() => setShowMenu(false)} other={other} />
        </div>
        <div className='menu-button' onClick={() => setShowMenu(true)}>
          <FaOutdent size={30} color='white' />
        </div>
      </HeaderContainer>
      <SubHeader />
    </>
  );
};
